/**
 * exportZipper.ts
 * Bundles separate per-document PDFs into a single ZIP so a user can export
 * every selected phiếu as its own file in one download (no backend needed).
 *
 * The whole existing pipeline is reused unchanged:
 *   - `applyPageNumbering` re-numbers a page list per document, so each phiếu
 *     in the ZIP shows "Trang i/N" for *that* phiếu (not the whole batch).
 *   - `generatePDF` renders one group of pages to a PDF blob.
 * Here we just call them once per document and pack the results with fflate.
 */

import { zipSync } from "fflate";
import { generatePDF, resolvePdfRenderScale, type PaperSetup } from "./printExporter";
import { applyPageNumbering } from "./docxRender";
import type { RenderedPage } from "@/types/print";

export interface ExportZipperOptions {
  /**
   * Override html2canvas scale. Prefer omit — auto-scale drops quality on
   * large batches so the tab does not OOM (scale 3 ≈ 9× pixel memory).
   */
  renderScale?: number;
  /** Base name for the ZIP archive (a date-stamp is appended). */
  zipBaseName?: string;
  paper?: PaperSetup;
  forcePaper?: boolean;
  /** Progress callback: completed page count → total page count. */
  onProgress?: (done: number, total: number) => void;
}

export interface GroupedPdfEntry {
  /** Per-document file stem; ".pdf" is added automatically. */
  fileName: string;
  /**
   * Pages of ONE document. NOT yet page-numbered: numbering is applied here
   * per entry so each phiếu numbers its own pages correctly.
   */
  pages: RenderedPage[];
}

/** Characters that are invalid in a filename across common OSes. */
const INVALID_FILENAME_CHARS = /[\\/:*?"<>|\u0000-\u001f]/g;

/** Strip unsafe chars, trim, cap length and guarantee a non-empty result. */
export function sanitizeFileName(name: string, fallback = "Phieu"): string {
  const cleaned = String(name ?? "")
    .replace(INVALID_FILENAME_CHARS, "_")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
  return cleaned || fallback;
}

/**
 * Render each document to its own PDF and pack all of them into one ZIP blob.
 * Page numbering is per document (see `applyPageNumbering`), so two phiếu of
 * three pages each appear as "Trang 1/3 … 3/3" in their own files.
 */
export async function exportPdfsAsZip(
  entries: GroupedPdfEntry[],
  options: ExportZipperOptions = {}
): Promise<{ blob: Blob; fileName: string }> {
  const totalPages = entries.reduce((n, e) => n + (e.pages?.length ?? 0), 0);
  // Shared auto-scale with generatePDF (scale 3 ≈ 9× memory — OOM on big ZIPs).
  const scale = resolvePdfRenderScale(totalPages, options.renderScale);
  const files: Record<string, Uint8Array> = {};
  let donePages = 0;

  for (const entry of entries) {
    if (!entry.pages || entry.pages.length === 0) continue;

    // Number each document independently.
    const numbered = applyPageNumbering(entry.pages);
    const pdfBlob = await generatePDF(numbered, {
      renderScale: scale,
      paper: options.paper,
      forcePaper: options.forcePaper,
      onProgress: (done, _total) => {
        options.onProgress?.(donePages + done, totalPages);
      },
    });
    donePages += numbered.length;

    // Guard against duplicate file names (two phiếu sharing one label).
    let stem = sanitizeFileName(entry.fileName);
    let suffix = 1;
    while (files[`${stem}.pdf`] !== undefined) {
      stem = `${sanitizeFileName(entry.fileName)}_${suffix++}`;
    }
    // generatePDF returns a Blob in the browser; tests may hand back a Uint8Array.
    const pdfBytes =
      pdfBlob instanceof Blob
        ? new Uint8Array(await pdfBlob.arrayBuffer())
        : (pdfBlob as Uint8Array);
    files[`${stem}.pdf`] = pdfBytes;
  }

  if (Object.keys(files).length === 0) {
    throw new Error("Không có phiếu nào để bỏ vào file ZIP.");
  }

  const data = zipSync(files, { level: 6 });
  // Wrap in a real Blob: works on the browser for download and in Node for tests.
  const blob = new Blob([data as unknown as BlobPart], { type: "application/zip" });

  const date = new Date().toISOString().slice(0, 10);
  const base = sanitizeFileName(options.zipBaseName ?? "PhieuGiaoHang", "PhieuGiaoHang");
  return { blob, fileName: `${base}-${date}.zip` };
}

/** Trigger a browser download for a blob with the given file name. */
export function downloadBlob(blob: Blob, fileName: string): void {
  // Empty MIME type breaks Chromium/Edge download managers for PDF/ZIP.
  let typed = blob;
  if (!blob.type || blob.type === "application/octet-stream") {
    const lower = fileName.toLowerCase();
    const mime = lower.endsWith(".pdf")
      ? "application/pdf"
      : lower.endsWith(".zip")
        ? "application/zip"
        : blob.type || "application/octet-stream";
    if (mime !== "application/octet-stream") {
      typed = new Blob([blob], { type: mime });
    }
  }
  const url = URL.createObjectURL(typed);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.rel = "noopener";
  a.style.display = "none";
  // Must be in the document for Chromium / Edge / Electron download managers.
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Delay revoke so the browser can finish reading the blob URL.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
